import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Chart, registerables } from 'chart.js';
import { BeatLoader } from 'react-spinners';
import '../../../../../styles/count_graphs.css';
import { url } from '../../../../../api/endpointURL';
import {
    transformDailyData,
    transformWeeklyData,
    transformMonthlyData,
    transformMonthlyDataForComparison,
    transformWeeklyDataForComparison,
    transformDailyDataForComparison,
    transformHourlyDataForComparison,
    getTotalCountVisitors,
    getCountPercentageIncrease,
    getTotalCountForCurrentYear,
    checkForEmptyData
} from '../baseSuiteUtils';
import MonthlyCountChart from './MonthlyCountGraph';
import WeeklyCountChart from './WeeklyCountGraph';
import DailyCountChart from './DailyCountGraph';
import HourlyCountChart from './HourlyCountGraph';

Chart.register(...registerables);

const CountAnalytics = ({
    hwid,
    site,
    selectedCamera,
    selectedCameraId = 'All',
    selectedYear,
    handleCountDataState,
    processedCameraData = []
}) => {
    const [month, setMonth] = useState();
    const [monthSelectedByUser, setMonthSelectedByUser] = useState(false);
    const [week, setWeek] = useState();
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState();
    const [selectedWeekStartDate, setSelectedWeekStartDate] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [dateSelectedByUser, setDateSelectedByUser] = useState();

    const [weekSelectedByUser, setWeekSelectedByUser] = useState(false);
    const [monthlyLoading, setmonthlyLoading] = useState(false);
    const [weeklyLoading, setWeeklyLoading] = useState(false);
    const [dailyLoading, setDailyLoading] = useState(false);
    const [hourlyLoading, setHourlyLoading] = useState(false); // Add hourly loading state

    const [aggregatedMonthlyData, setAggregatedMonthlyData] = useState([]);
    const [aggregatedWeeklyData, setAggregatedWeeklyData] = useState([]);
    const [aggregatedDailyData, setAggregatedDailyData] = useState([]);
    const [aggregatedHourlyData, setAggregatedHourlyData] = useState([]); // Add hourly data state

    // Store raw data for comparison mode
    const [rawMonthlyData, setRawMonthlyData] = useState([]);
    const [rawWeeklyData, setRawWeeklyData] = useState([]);
    const [rawDailyData, setRawDailyData] = useState([]);
    const [rawHourlyData, setRawHourlyData] = useState([]);

    // Add error states
    const [monthlyError, setMonthlyError] = useState(false);
    const [weeklyError, setWeeklyError] = useState(false);
    const [dailyError, setDailyError] = useState(false);
    const [hourlyError, setHourlyError] = useState(false); // Add hourly error state

    const monthlyAbortControllerRef = useRef(null);
    const weeklyAbortControllerRef = useRef(null);
    const dailyAbortControllerRef = useRef(null);
    const hourlyAbortControllerRef = useRef(null); // Add hourly abort controller

    const monthlyRequestIdRef = useRef(0);
    const weeklyRequestIdRef = useRef(0);
    const dailyRequestIdRef = useRef(0);
    const hourlyRequestIdRef = useRef(0); // Add hourly request ID

    const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
    const [showCount, setShowCount] = useState(true);

    // Individual traffic view toggles for each graph
    const [isMonthlyTrafficView, setIsMonthlyTrafficView] = useState(false);
    const [isWeeklyTrafficView, setIsWeeklyTrafficView] = useState(false);
    const [isDailyTrafficView, setIsDailyTrafficView] = useState(false);
    const [isHourlyTrafficView, setIsHourlyTrafficView] = useState(false);

    // Camera comparison mode toggles for each graph
    const [isMonthlyCompareMode, setIsMonthlyCompareMode] = useState(false);
    const [isWeeklyCompareMode, setIsWeeklyCompareMode] = useState(false);
    const [isDailyCompareMode, setIsDailyCompareMode] = useState(false);
    const [isHourlyCompareMode, setIsHourlyCompareMode] = useState(false);

    const prevCameraRef = useRef(selectedCameraId);

    useEffect(() => {
        const cameraChanged = prevCameraRef.current !== selectedCameraId;
        prevCameraRef.current = selectedCameraId;

        if (cameraChanged) {
            setInitialFetchCompleted(false);
            // Reset error states when camera changes
            setMonthlyError(false);
            setWeeklyError(false);
            setDailyError(false);
            setHourlyError(false); // Reset hourly error
        }
    }, [selectedCameraId]);

    const hasActualData = useCallback(() => {
        const hasData = checkForEmptyData(aggregatedMonthlyData);
        if (!hasData) {
            return false;
        } else {
            return true;
        }
    }, [aggregatedMonthlyData]);

    const chartRef = useRef(null);

    const totalIn = useMemo(() =>
        aggregatedMonthlyData.length > 0 ? getTotalCountForCurrentYear(aggregatedMonthlyData, 'Line Crossing') : 0,
        [aggregatedMonthlyData]
    );

    const totalOut = useMemo(() =>
        aggregatedMonthlyData.length > 0 ? getTotalCountForCurrentYear(aggregatedMonthlyData, 'Reverse Line Crossing') : 0,
        [aggregatedMonthlyData]
    );

    // Enhanced error handling function
    const handleApiError = (error, dataType, setErrorState) => {
        console.warn(`${dataType} data unavailable:`, error.message);
        setErrorState(true);
        
        // Don't throw error for 500 responses - treat as no data available
        if (error.message.includes('500')) {
            // console.log(`No ${dataType} data available for this site/camera combination`);
            return [];
        }
        
        // Only log as error for non-500 status codes
        if (!error.message.includes('Server responded with status: 500')) {
            console.error(`Error fetching ${dataType} data:`, error);
        }
        
        return [];
    };

    // Fetch monthly count data
    const fetchMonthlyCountData = useCallback(async () => {
        if (!hwid || !selectedYear) return;

        if (monthlyAbortControllerRef.current) {
            monthlyAbortControllerRef.current.abort();
        }

        monthlyAbortControllerRef.current = new AbortController();
        const { signal } = monthlyAbortControllerRef.current;

        monthlyRequestIdRef.current += 1;
        const currentRequestId = monthlyRequestIdRef.current;
        try {
            setmonthlyLoading(true);
            setMonthlyError(false);

            const response = await fetch(`${url}/monthly-count?year=${selectedYear}&hwid=${hwid}`, { signal });
            
            if (!response.ok) {
                if (response.status === 500) {
                    // Treat 500 as "no data available" rather than an error
                    // console.log('No monthly data available for this site');
                    if (currentRequestId === monthlyRequestIdRef.current) {
                        setAggregatedMonthlyData([]);
                        setInitialFetchCompleted(true);
                    }
                    return;
                }
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const responseData = await response.json();

            if (currentRequestId === monthlyRequestIdRef.current) {
                // Store raw data for comparison mode
                setRawMonthlyData(responseData);

                // Process data for single camera view
                const processedData = transformMonthlyData(responseData, selectedCameraId);
                setAggregatedMonthlyData(processedData);
                setInitialFetchCompleted(true);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log('Monthly data request was cancelled');
                return;
            }
            
            if (currentRequestId === monthlyRequestIdRef.current) {
                handleApiError(error, 'monthly', setMonthlyError);
                setAggregatedMonthlyData([]);
                setInitialFetchCompleted(true);
            }
        } finally {
            if (currentRequestId === monthlyRequestIdRef.current) {
                setmonthlyLoading(false); 
            }
        }
    }, [selectedYear, hwid, selectedCameraId]);

    // Fetch weekly count data
    const fetchWeeklyCountData = useCallback(async (monthNumber) => {
        if (!hwid || !selectedYear) return;

        if (weeklyAbortControllerRef.current) {
            weeklyAbortControllerRef.current.abort();
        }

        weeklyAbortControllerRef.current = new AbortController();
        const { signal } = weeklyAbortControllerRef.current;

        weeklyRequestIdRef.current += 1;
        const currentRequestId = weeklyRequestIdRef.current;

        try {
            setWeeklyLoading(true);
            setWeeklyError(false);

            const response = await fetch(`${url}/weekly-count?year=${selectedYear}&month=${monthNumber}&hwid=${hwid}`, { signal });
            
            if (!response.ok) {
                if (response.status === 500) {
                    // console.log('No weekly data available for this site/month');
                    if (currentRequestId === weeklyRequestIdRef.current) {
                        setAggregatedWeeklyData([]);
                    }
                    return;
                }
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const responseData = await response.json();

            if (currentRequestId === weeklyRequestIdRef.current) {
                // Store raw data for comparison mode
                setRawWeeklyData(responseData);

                // Process data for single camera view
                const processedData = transformWeeklyData(responseData, selectedCameraId);
                setAggregatedWeeklyData(processedData);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log("Weekly data request was cancelled");
                return;
            }
            
            if (currentRequestId === weeklyRequestIdRef.current) {
                handleApiError(error, 'weekly', setWeeklyError);
                setAggregatedWeeklyData([]);
            }
        } finally {
            if (currentRequestId === weeklyRequestIdRef.current) {
                setWeeklyLoading(false); 
            }
        }
    }, [selectedYear, hwid, selectedCameraId, month]);

    const fetchDailyCountData = useCallback(async (weekNumber) => {
        if (!hwid || !selectedYear || !month || !week) return;

        if (dailyAbortControllerRef.current) {
            dailyAbortControllerRef.current.abort();
        }

        dailyAbortControllerRef.current = new AbortController();
        const { signal } = dailyAbortControllerRef.current;

        dailyRequestIdRef.current += 1;
        const currentRequestId = dailyRequestIdRef.current;
        try {
            setDailyLoading(true);
            setDailyError(false);

            const response = await fetch(`${url}/daily-count?year=${selectedYear}&month=${month}&week=${weekNumber}&hwid=${hwid}`, { signal });
            
            if (!response.ok) {
                if (response.status === 500) {
                    // console.log('No daily data available for this site/week');
                    if (currentRequestId === dailyRequestIdRef.current) {
                        setAggregatedDailyData([]);
                    }
                    return;
                }
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const responseData = await response.json();

            if (currentRequestId === dailyRequestIdRef.current) {
                // Store raw data for comparison mode
                setRawDailyData(responseData);

                // Process data for single camera view
                const processedData = transformDailyData(responseData, selectedCameraId);
                setAggregatedDailyData(processedData);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log('Daily data request was cancelled');
                return;
            }
            
            if (currentRequestId === dailyRequestIdRef.current) {
                handleApiError(error, 'daily', setDailyError);
                setAggregatedDailyData([]);
            }
        } finally {
            if (currentRequestId === dailyRequestIdRef.current) {
                setDailyLoading(false); 
            }
        }
    }, [selectedYear, month, week, hwid, selectedCameraId]);

    // Add fetchHourlyCountData function
    const fetchHourlyCountData = useCallback(async (year, month, day) => {
        if (!hwid || !year || !month || !day) return;

        if (hourlyAbortControllerRef.current) {
            hourlyAbortControllerRef.current.abort();
        }

        hourlyAbortControllerRef.current = new AbortController();
        const { signal } = hourlyAbortControllerRef.current;

        hourlyRequestIdRef.current += 1;
        const currentRequestId = hourlyRequestIdRef.current;

        try {
            setHourlyLoading(true);
            setHourlyError(false);

            const response = await fetch(`${url}/hourly-count?year=${year}&month=${month}&day=${day}&hwid=${hwid}`, { signal });
            
            if (!response.ok) {
                if (response.status === 500) {
                    // console.log('No hourly data available for this site/day');
                    if (currentRequestId === hourlyRequestIdRef.current) {
                        setAggregatedHourlyData([]);
                    }
                    return;
                }
                throw new Error(`Server responded with status: ${response.status}`);
            }
            
            const responseData = await response.json();

            if (currentRequestId === hourlyRequestIdRef.current) {
                // Store raw data for comparison mode
                setRawHourlyData(responseData);
                setAggregatedHourlyData(responseData);
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // console.log('Hourly data request was cancelled');
                return;
            }
            
            if (currentRequestId === hourlyRequestIdRef.current) {
                handleApiError(error, 'hourly', setHourlyError);
                setAggregatedHourlyData([]);
            }
        } finally {
            if (currentRequestId === hourlyRequestIdRef.current) {
                setHourlyLoading(false); 
            }
        }
    }, [hwid]);

    const handleMonthlyBarClick = useCallback((monthNumber) => {
        setMonthSelectedByUser(true);
        setMonth(monthNumber);
        fetchWeeklyCountData(monthNumber);
    }, [fetchWeeklyCountData]);

    const handleWeeklyBarClick = useCallback((weekNumber) => {
        setWeekSelectedByUser(true);
        setWeek(weekNumber);
        fetchDailyCountData(weekNumber);
    }, [fetchDailyCountData]);

    // Add handleDailyBarClick to fetch hourly data when a day is selected
    const handleDailyBarClick = useCallback((dayData) => {
        setDateSelectedByUser(true);
        setSelectedDate(dayData.day_number);
        setSelectedDayOfWeek(dayData.day);
        // Fetch hourly data for the selected day
        fetchHourlyCountData(selectedYear, month, dayData.day_number);
    }, [fetchHourlyCountData, selectedYear, month]);

    const handleHourlyBarClick = useCallback((hourData) => {
        // console.log(`Selected hour: ${hourData.hour_display}`, hourData);
        // Add any additional logic for hour selection here
    }, []);

    useEffect(() => {
        return () => {
            if (monthlyAbortControllerRef.current) monthlyAbortControllerRef.current.abort();
            if (weeklyAbortControllerRef.current) weeklyAbortControllerRef.current.abort();
            if (dailyAbortControllerRef.current) dailyAbortControllerRef.current.abort();
            if (hourlyAbortControllerRef.current) hourlyAbortControllerRef.current.abort(); // Cleanup hourly requests
        };
    }, []);

    useEffect(() => {
        if (initialFetchCompleted) {
            const hasData = hasActualData();
            setShowCount(hasData);

            if (handleCountDataState) {
                handleCountDataState(hasData);
            }
        }
    }, [initialFetchCompleted, hasActualData, handleCountDataState]);

    useEffect(() => {
        if (selectedYear && hwid) {
            fetchMonthlyCountData();
        }
    }, [selectedYear, hwid, selectedCameraId, fetchMonthlyCountData]);

    // update selected month as the latest month in the aggregated monthly data when selecting a camera
    useEffect(() => {
        if (!monthSelectedByUser && aggregatedMonthlyData.length) {
            const latestMonthWithData = [...aggregatedMonthlyData]
                .reverse()
                .find(month => month.total_count > 0);

            if (latestMonthWithData) {
                setMonth(latestMonthWithData.month);
            }
        }
    }, [aggregatedMonthlyData, monthSelectedByUser]);

    // Fetch weekly data when month changes (removed aggregatedMonthlyData to prevent loop)
    useEffect(() => {
        if (month) {
            fetchWeeklyCountData(month);
        }
    }, [selectedYear, hwid, selectedCameraId, month, fetchWeeklyCountData]);

    // update selected week as the latest week in the aggregated weekly data when selecting a camera
    useEffect(() => {
        if (!weekSelectedByUser && aggregatedWeeklyData.length) {
            const latestWeekWithData = [...aggregatedWeeklyData]
                .reverse()
                .find(week => week.total_count > 0);

            if (latestWeekWithData) {
                setWeek(latestWeekWithData.week);
                setSelectedWeekStartDate(latestWeekWithData.week_start_date);
            }
        }
    }, [aggregatedWeeklyData, weekSelectedByUser]);

    // Fetch daily data when week changes (removed aggregatedWeeklyData to prevent loop)
    useEffect(() => {
        if (week) {
            fetchDailyCountData(week);
        }
    }, [month, hwid, selectedCameraId, selectedYear, week, fetchDailyCountData]);

    useEffect(() => {
        if (aggregatedDailyData.length) {
            const latestDayWithData = [...aggregatedDailyData]
                .reverse()
                .find(day => day.event_types['Line Crossing'] > 0);

            if (latestDayWithData) {
                setSelectedDate(latestDayWithData.day_number);
                setSelectedDayOfWeek(latestDayWithData.day);
                // Automatically fetch hourly data for the latest day with data
                fetchHourlyCountData(selectedYear, month, latestDayWithData.day_number);
            }
        }
    }, [aggregatedDailyData, dateSelectedByUser, fetchHourlyCountData, selectedYear, month]);

    // Show "No data" message instead of hiding component entirely
    const shouldShowNoDataMessage = initialFetchCompleted && !hasActualData() && !monthlyLoading;

    return (
        <>
            {showCount ? (
                <>
                    <div className="graphs-container">
                        <div className='monthly-bar-chart-container'>
                            <MonthlyCountChart
                                selectedYear={selectedYear}
                                monthlyCountData={aggregatedMonthlyData}
                                rawMonthlyData={rawMonthlyData}
                                monthlyDataLoading={monthlyLoading}
                                selectedMonth={month}
                                handleMonthlyBarClick={handleMonthlyBarClick}
                                monthlyVisitors={getTotalCountVisitors(aggregatedMonthlyData, month, 'month')}
                                monthlyVisitorPercentageIncrease={getCountPercentageIncrease(aggregatedMonthlyData, month, 'month', 'Line Crossing')}
                                isTrafficView={isMonthlyTrafficView}
                                setIsTrafficView={setIsMonthlyTrafficView}
                                isCompareMode={isMonthlyCompareMode}
                                setIsCompareMode={setIsMonthlyCompareMode}
                                processedCameraData={processedCameraData}
                            />
                        </div>

                        <div className='weekly-bar-chart-container' >
                            <WeeklyCountChart
                                selectedYear={selectedYear}
                                weeklyCountData={aggregatedWeeklyData}
                                rawWeeklyData={rawWeeklyData}
                                weeklyDataLoading={weeklyLoading}
                                selectedMonth={month}
                                selectedWeek={week}
                                handleWeeklyBarClick={handleWeeklyBarClick}
                                weeklyVisitors={getTotalCountVisitors(aggregatedWeeklyData, week, 'week')}
                                weeklyVisitorPercentageIncrease={getCountPercentageIncrease(aggregatedWeeklyData, week, 'week', 'Line Crossing')}
                                isTrafficView={isWeeklyTrafficView}
                                setIsTrafficView={setIsWeeklyTrafficView}
                                isCompareMode={isWeeklyCompareMode}
                                setIsCompareMode={setIsWeeklyCompareMode}
                                processedCameraData={processedCameraData}
                            />
                        </div>

                        <div className='daily-bar-chart-container'>
                            <DailyCountChart
                                selectedYear={selectedYear}
                                dailyCountData={aggregatedDailyData}
                                rawDailyData={rawDailyData}
                                dailyDataLoading={dailyLoading}
                                selectedMonth={month}
                                selectedWeekStartDate={selectedWeekStartDate}
                                selectedDayOfWeek={selectedDayOfWeek}
                                selectedDate={selectedDate}
                                handleDailyBarClick={handleDailyBarClick}
                                dailyVisitors={getTotalCountVisitors(aggregatedDailyData, selectedDate, 'day_number')}
                                dailyVisitorPercentageIncrease={getCountPercentageIncrease(aggregatedDailyData, selectedDate, 'day_number', 'Line Crossing')}
                                isTrafficView={isDailyTrafficView}
                                setIsTrafficView={setIsDailyTrafficView}
                                isCompareMode={isDailyCompareMode}
                                setIsCompareMode={setIsDailyCompareMode}
                                processedCameraData={processedCameraData}

                                hourlyCountData={aggregatedHourlyData}
                                rawHourlyData={rawHourlyData}
                                hourlyDataLoading={hourlyLoading}
                                handleHourlyBarClick={handleHourlyBarClick}
                                fetchHourlyCountData={fetchHourlyCountData}
                                isHourlyTrafficView={isHourlyTrafficView}
                                setIsHourlyTrafficView={setIsHourlyTrafficView}
                                isHourlyCompareMode={isHourlyCompareMode}
                                setIsHourlyCompareMode={setIsHourlyCompareMode}
                            />
                        </div>
                    </div>
                </>
            ) : shouldShowNoDataMessage ? (
                <div className="no-data-message" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '2rem',
                    color: '#666',
                    fontSize: '1.1rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    margin: '1rem 0'
                }}>
                    No count data available for the selected site and camera.
                </div>
            ) : (
                <div style={{ display: 'none' }}></div>
            )}
        </>
    );
};

export default CountAnalytics;